import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Hexagon, Lock, Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import "./Home.css";

// ==========================================================
// Trava de acesso ao workspace pessoal.
// Não é autenticação real — é só uma barreira para evitar
// acesso casual. Mantenha este PIN único, sem reutilizar
// em outros serviços.
// ==========================================================
const ACCESS_PIN = "246810"; // ← troque pelo seu PIN
const USER_NAME = "Marcus";
const SESSION_SHORT_DAYS = 1;   // sessão sem "manter conectado"
const SESSION_LONG_DAYS = 30;   // sessão com "manter conectado"
// ==========================================================

export default function Home() {
    const navigate = useNavigate();

    const [inputPin, setInputPin] = useState("");
    const [error, setError] = useState("");
    const [showPin, setShowPin] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(true); // padrão: manter conectado

    const timerRef = useRef(null);

    // --- VERIFICAÇÃO DE SESSÃO AO ABRIR O APP ---
    useEffect(() => {
        const isAuth = localStorage.getItem("isAuth");
        const expiresAt = localStorage.getItem("isAuthExpiresAt");

        if (isAuth === "true") {
            // Se não tem data de expiração, considera sessão válida (compatibilidade)
            if (!expiresAt) {
                navigate("/dashboard");
                return;
            }
            // Se tem data, verifica se ainda é válida
            if (Date.now() < Number(expiresAt)) {
                navigate("/dashboard");
            } else {
                // Sessão expirou — limpa tudo
                localStorage.removeItem("isAuth");
                localStorage.removeItem("isAuthExpiresAt");
            }
        }
    }, [navigate]);

    // --- CLEANUP DO TIMEOUT ---
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    // --- LOGIN ---
    const handleLogin = (e) => {
        e.preventDefault();
        setError("");

        if (!inputPin.trim()) {
            setError("Digite o PIN para continuar.");
            return;
        }

        setIsLoading(true);

        timerRef.current = setTimeout(() => {
            if (inputPin === ACCESS_PIN) {
                // Define a data de expiração conforme "manter conectado"
                const days = rememberMe ? SESSION_LONG_DAYS : SESSION_SHORT_DAYS;
                const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;

                localStorage.setItem("isAuth", "true");
                localStorage.setItem("isAuthExpiresAt", String(expiresAt));

                // Define o nome do workspace se ainda não existir
                if (!localStorage.getItem("myWorkspaceName")) {
                    localStorage.setItem("myWorkspaceName", USER_NAME);
                }

                navigate("/dashboard");
            } else {
                setError("PIN incorreto. Tente novamente.");
                setIsLoading(false);
                setInputPin("");
            }
        }, 400);
    };

    return (
        <div className="home-container">
            <div className="background-glow"></div>

            <div className="login-card">
                <div className="logo-area">
                    <Hexagon size={56} className="logo-icon" strokeWidth={2} />
                </div>

                <h1 className="brand-title">ViniHub</h1>
                <p className="subtitle">Digite o PIN para acessar</p>

                <form onSubmit={handleLogin} className="login-form">
                    <div className="login-input-group">
                        <label>PIN de Acesso</label>
                        <div className="login-input-wrapper">
                            <Lock size={18} className="login-input-icon" />
                            <input
                                type={showPin ? "text" : "password"}
                                placeholder="••••••"
                                value={inputPin}
                                onChange={(e) => {
                                    const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    setInputPin(onlyDigits);
                                    setError("");
                                }}
                                inputMode="numeric"
                                autoComplete="off"
                                autoFocus
                            />
                            <button
                                type="button"
                                className="btn-toggle-pass"
                                onClick={() => setShowPin(!showPin)}
                                tabIndex={-1}
                                aria-label={showPin ? "Ocultar PIN" : "Mostrar PIN"}
                            >
                                {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Checkbox: Manter conectado */}
                    <label className="remember-me">
                        <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <span>Manter conectado por 30 dias</span>
                    </label>

                    {error && <div className="login-error-msg">{error}</div>}

                    <button type="submit" className="btn-login" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Loader2 size={20} className="spinner" /> Verificando...
                            </>
                        ) : (
                            <>
                                Entrar <ArrowRight size={20} />
                            </>
                        )}
                    </button>
                </form>

                <div className="footer-links">
                    <span className="version">v2.0.0 • Acesso Restrito</span>
                </div>
            </div>
        </div>
    );
}